import React from 'react';
import PropTypes from 'prop-types';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Table from 'react-bootstrap/Table';
import Card from 'react-bootstrap/Card';
import { Link } from 'react-router-dom';
import { FormattedNumber, FormattedDate } from 'react-intl';
import { getAmendmentVoteStyle, rippleTime2UnixTime } from '../common/Helpers';
import CardTitleContainer from '../lib/CardTitleContainer';
import XBadge from '../lib/XBadge';
import XIcon from '../lib/XIcon';
import XCopyButton from '../lib/XCopyButton';

const AmendmentInfo = (props) => {
  const { amendment, error } = props;
  const fragment = amendment.name ? `#${amendment.name.toLowerCase()}` : '';

  if (error) {
    return <div className="error">{error}</div>
  }

  return (
  <div>
    <Card className={amendment?.enabled ? `shadow-sm border-success` : ``}>
      <Card.Body>
        <Card.Title>
          <CardTitleContainer icon="cogs" title="Amendment summary" />
        </Card.Title>
        <Row>
          <Col xs={12} md={8}>
            <Table responsive>
              <thead>
                <tr className='text-nowrap'>
                  <th>Amendment:</th>
                  <th>
                    <Link to={`/amendment/${amendment.name}`}>{amendment.name}</Link>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className='text-nowrap'>
                  <td>Amendment ID:</td>
                  <td className="hash text-muted text-nowrap">
                    {amendment?.amendment_id}
                    <XCopyButton text={amendment?.amendment_id} />
                  </td>
                </tr>
                <tr className='text-nowrap'>
                  <td>Introduced in:</td>
                  <td>
                    <span>
                      { amendment.introduced ? `v${amendment.introduced}` : 'TBD' }
                    </span>
                  </td>
                </tr>
                { amendment.tx_hash &&
                <tr className='text-nowrap'>
                  <td>Enabled in tx:</td>
                  <td>
                    <span className="hash">
                      <Link to={`/tx/${amendment.tx_hash}`}>{amendment.tx_hash}</Link>
                    </span>
                  </td>
                </tr>
                }
                <tr className='text-nowrap'>
                  <td>Details:</td>
                  <td>
                    <a href={`https://xrpl.org/resources/known-amendments${fragment}`} rel="nofollow noopener noreferrer" target="_blank">
                      https://xrpl.org/resources/known-amendments{fragment}
                    </a>
                  </td>
                </tr>
                {amendment.xls && amendment.xls_url &&
                <tr className='text-nowrap'>
                  <td>XLS spec:</td>
                  <td>
                    <span className='mr-2'>
                      <a href={amendment.xls_url} rel="nofollow noopener noreferrer" target="_blank">{amendment.xls_url}</a>
                    </span>
                    <XBadge variant="primary">{amendment.xls}</XBadge>
                  </td>
                </tr>
                }
              </tbody>
            </Table>
          </Col>

          <Col xs={12} md={4}>
            <Table responsive>
              <thead>
                <tr>
                  <th>Status</th>
                  <th>
                    { amendment.enabled ?
                    <XBadge variant="success">ENABLED</XBadge> :
                    <XBadge variant="danger">NOT ENABLED</XBadge>
                    }
                    { amendment.deprecated &&
                    <XBadge variant="dark" className="ml-1">DEPRECATED</XBadge>
                    }
                  </th>
                </tr>
              </thead>
              <tbody>
                { amendment?.threshold >= 0 &&
                <tr className='text-nowrap'>
                  <td>Threshold:</td>
                  <td><span className="hash">{amendment?.threshold}/{amendment?.validations}</span> votes</td>
                </tr>
                }

                {!amendment.enabled_on &&
                <tr>
                  <td>ETA:</td>
                  <td>
                    {!amendment.majority &&
                    <span>
                      <XBadge variant="warning">VOTING</XBadge>
                    </span>
                    }
                    {amendment.majority &&
                    <span className="mark">
                      <FormattedDate
                        value={ rippleTime2UnixTime(amendment.majority) * 1000 + (14 * 24 * 3600 * 1000) }
                        year='numeric'
                        month='short'
                        day='2-digit'
                        hour='2-digit'
                        minute='2-digit'
                        second='2-digit'
                        timeZone='UTC'
                        timeZoneName='short'
                      />
                    </span>
                    }

                  </td>
                </tr>
                }

                { amendment.count >= 0 &&
                <tr
                  className={ amendment.enabled ? '' : getAmendmentVoteStyle(amendment.count, amendment.threshold) }
                >
                  <td>Consensus:</td>
                  <td>
                    {amendment?.count >= 0 && amendment?.validations >= 0 &&
                    <>
                      <XBadge className="text-monospace mr-2">
                        <FormattedNumber value={(amendment.count/amendment.validations) * 100} maximumFractionDigits={2} />
                        <XIcon icon='percent' className="text-dark"/>
                      </XBadge>

                      <XBadge variant="success" className="text-monospace mr-2">
                        {amendment.count}
                        <XIcon icon='vote-yea' className="text-success ml-1"/>
                      </XBadge>

                      <XBadge variant="danger" className="text-monospace mr-2">
                        {amendment.validations - amendment.count}
                        <XIcon icon='vote-nay' className="text-danger ml-1"/>
                      </XBadge>
                    </>
                    }
                  </td>
                </tr>
                }
                { amendment.enabled_on &&
                <tr className='table-success'>
                  <td>Enabled on:</td>
                  <td>
                    <span>
                      <FormattedDate
                        value={amendment.enabled_on}
                        year='numeric'
                        month='short'
                        day='2-digit'
                        hour='2-digit'
                        minute='2-digit'
                        second='2-digit'
                        timeZone='UTC'
                        timeZoneName='short'
                      />
                    </span>
                  </td>
                </tr>
                }
              </tbody>
            </Table>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  </div>
  );
}


AmendmentInfo.propTypes = {
  amendment: PropTypes.object.isRequired,
  error: PropTypes.object,
}

export default AmendmentInfo;
